import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { SidebarTrigger } from 'components/ui/sidebar';
import { Separator } from 'components/ui/separator';
import { Button } from 'components/ui/button';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from 'components/ui/breadcrumb';
import { Moon, Sun } from 'lucide-react';
import { useTheme } from 'components/ThemeProvider';
import AppBarUser from 'components/AppBarUser';
import { cn } from 'lib/utils';
import './AppBar.css';

/**
 * Shared frosted app bar for any page.
 *
 * @param {Array<{ label: string, to?: string, hideOnMobile?: boolean }>} [breadcrumbs]
 * @param {React.ReactNode} [actions] Extra controls before the theme toggle
 * @param {boolean} [showThemeToggle=true]
 * @param {boolean} [showProfile=true]
 * @param {'fixed'|'inset'} [variant='fixed'] inset = sits inside a rounded page panel
 * @param {string} [className]
 */
export default function AppBar({
  breadcrumbs = [{ label: 'Page' }],
  actions = null,
  showThemeToggle = true,
  showProfile = true,
  variant = 'fixed',
  className,
}) {
  const { isDark, toggleTheme } = useTheme();
  const crumbs = Array.isArray(breadcrumbs) && breadcrumbs.length > 0
    ? breadcrumbs
    : [{ label: 'Page' }];

  return (
    <header
      className={cn(
        'app-bar',
        variant === 'inset' && 'app-bar--inset',
        className
      )}
    >
      <SidebarTrigger className="-ml-1" />
      <Separator orientation="vertical" className="mr-1 hidden h-4 sm:block" />
      <Breadcrumb className="min-w-0 flex-1">
        <BreadcrumbList>
          {crumbs.map((crumb, index) => {
            const isLast = index === crumbs.length - 1;
            const hideOnMobile = crumb.hideOnMobile ?? (!isLast && crumbs.length > 1);
            const prevHideOnMobile =
              index > 0
                ? (crumbs[index - 1].hideOnMobile ?? (index - 1 < crumbs.length - 1 && crumbs.length > 1))
                : false;

            return (
              <Fragment key={`${crumb.label}-${index}`}>
                {index > 0 ? (
                  <BreadcrumbSeparator
                    className={prevHideOnMobile ? 'hidden sm:inline-flex' : undefined}
                  />
                ) : null}
                <BreadcrumbItem
                  className={hideOnMobile ? 'hidden sm:inline-flex' : undefined}
                >
                  {isLast ? (
                    <BreadcrumbPage className="truncate font-semibold">
                      {crumb.label}
                    </BreadcrumbPage>
                  ) : crumb.to ? (
                    <BreadcrumbLink asChild>
                      <Link
                        to={crumb.to}
                        className="truncate text-muted-foreground hover:text-foreground"
                      >
                        {crumb.label}
                      </Link>
                    </BreadcrumbLink>
                  ) : (
                    <span className="truncate text-muted-foreground">{crumb.label}</span>
                  )}
                </BreadcrumbItem>
              </Fragment>
            );
          })}
        </BreadcrumbList>
      </Breadcrumb>

      <div className="ml-auto flex items-center gap-1">
        {actions}
        {showThemeToggle ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="app-bar__action"
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            aria-pressed={isDark}
            onClick={toggleTheme}
          >
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
        ) : null}
        {showProfile ? <AppBarUser /> : null}
      </div>
    </header>
  );
}
